import { cn, modalWidthClass } from "@/lib/utils";
import axios from "axios";
import cookie from "cookie";
import dayjs from "dayjs";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { ComponentProps, useEffect, useMemo, useState } from "react";

import { MixModal } from "./MixModal";

const MODAL_CONFIGS = {
    landing: {
        url: "https://mix.day/archive",
        button: {
            text: "자세히 알아보기",
            bgColor: "#333333",
            bgHoverColor: "#212121"
        }
    },
    images: {
        main: "/img/modal/archive_img_250811.webp",
        text: {
            pc: "/img/modal/archive_text_pc_250811.webp",
            mobile: "/img/modal/archive_text_mo_250811.webp"
        }
    }
} as const;

export const IntervalModal = ({
    startDate: $startDate,
    endDate: $endDate,
    interval,
    targetPages = undefined,
    isVisibleInExtension = false,
    ...props
}: {
    /**
     * 노출 주기 : N일 간격으로 모달 노출
     */
    interval: number;
    /**
     * 노출 시작일 : YYYY-MM-DD
     */
    startDate: string;
    /**
     * 노출 종료일 : YYYY-MM-DD (없으면 무기한 노출)
     */
    endDate?: string;
    /**
     * 특정 페이지에만 노출할 경우 사용 (없으면 모든 페이지에서 노출)
     */
    targetPages?: string[];
    /**
     * 크롬 확장프로그램 에서 표시할지 여부 (디폴트로 표시 안함)
     */
    isVisibleInExtension?: boolean;
} & Omit<ComponentProps<typeof MixModal>, "isOpen">) => {
    const router = useRouter();

    const [isOpen, setIsOpen] = useState(false);
    const [mainImgLoaded, setMainImgLoaded] = useState(false);

    const today = useMemo(() => dayjs(), []);

    const startDate = useMemo(
        () => ($startDate ? dayjs($startDate) : dayjs()),
        [$startDate]
    );

    const endDate = useMemo(
        () => ($endDate ? dayjs($endDate) : null),
        [$endDate]
    );

    const handleBtnClick = () => {
        axios.post("/api/hide_modal", {
            interval
        });
        setIsOpen(false);
    };

    useEffect(() => {
        // Guard against SSR
        if (typeof document === "undefined") return;

        const key = "mix/modal";
        const cookies = document.cookie;
        const exposed = cookie.parse(cookies)[key] === "true";

        const inRange =
            today.isAfter(startDate) &&
            (!endDate ? true : today.isBefore(endDate));

        const isTargetPage = !targetPages
            ? true
            : targetPages.includes(router.pathname);

        const isEnabledInExtension =
            process.env.BUILD_TARGET !== "extension" || isVisibleInExtension;

        if (!exposed && inRange && isTargetPage && isEnabledInExtension) {
            setIsOpen(true);
        }
    }, [
        today,
        startDate,
        endDate,
        router.pathname,
        targetPages,
        isVisibleInExtension
    ]);

    return (
        <MixModal
            isOpen={isOpen}
            onRequestClose={() => setIsOpen(false)}
            {...props}
        >
            <div
                className={cn(
                    `flex flex-row overflow-hidden rounded-small bg-white
                    text-black shadow-[0px_0px_16px_0px_rgba(0,0,0,0.32)]
                    dark:bg-grey-100 dark:text-white md:flex-col`,
                    modalWidthClass
                )}
            >
                <div
                    className={cn(
                        "relative h-[359px] w-[335px]",
                        !mainImgLoaded && "bg-grey-100"
                    )}
                >
                    <Image
                        alt="아카이브 프로모션 메인 이미지"
                        src={MODAL_CONFIGS.images.main}
                        fill={true}
                        preload={true}
                        onLoad={() => setMainImgLoaded(true)}
                    />
                </div>
                <div
                    className="flex flex-col justify-between gap-5 px-[18px]
                        py-7 md:gap-[26px] md:p-6"
                >
                    <div
                        className="relative hidden h-[135px] w-[276px] md:block"
                    >
                        <Image
                            alt="아카이브 프로모션 설명 텍스트 (모바일)"
                            src={MODAL_CONFIGS.images.text.mobile}
                            fill={true}
                            preload={true}
                        />
                    </div>
                    <div
                        className="relative block h-[199px] w-[296px] md:hidden"
                    >
                        <Image
                            alt="아카이브 프로모션 설명 텍스트 (PC)"
                            src={MODAL_CONFIGS.images.text.pc}
                            fill={true}
                            preload={true}
                        />
                    </div>
                    <div className="flex flex-col items-center gap-4">
                        <Link
                            className={cn(
                                `flex w-full items-center justify-center
                                rounded-small px-[10px] py-[18px] text-lg
                                font-bold leading-none text-common-white-main`,
                                today.isBefore("2024-07-17")
                                    ? "bg-[#0095F7] hover:bg-[#0085e5]"
                                    : "bg-[#333333] hover:bg-[#212121]"
                            )}
                            href={MODAL_CONFIGS.landing.url}
                            onClick={handleBtnClick}
                        >
                            {today.isBefore("2024-07-17")
                                ? "얼리버드 특가 확인하기"
                                : MODAL_CONFIGS.landing.button.text}
                        </Link>
                        <span
                            className="w-fit cursor-pointer text-sm font-medium
                                leading-none text-text-secondary underline"
                            onClick={handleBtnClick}
                        >
                            {interval === 1
                                ? "오늘 하루 그만보기"
                                : `${interval}일간 보지 않기`}
                        </span>
                    </div>
                </div>
            </div>
        </MixModal>
    );
};
